import { FormInstance } from 'antd';
import { useAuthContext } from 'contexts/AuthContext';
import dayjs from 'dayjs';
import useIndexedDbUploadDraft from 'hooks/useIndexedDbUploadDraft';
import useMinimizablePersistedDraft from 'hooks/useMinimizablePersistedDraft';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  BeneficiaryFormType,
  SiteContactInfo,
} from 'views/dossiers/ajout/forms/Beneficiary.form';
import { ProjectInformationsFormType } from 'views/dossiers/ajout/forms/ProjectInformations.form';
import {
  SiteAddressInformationsType,
  SiteContactInformationsType,
  SiteInformationsType,
} from 'views/dossiers/ajout/forms/SiteInformations.form';

const DRAFT_STORAGE_NAMESPACE = 'hellio:forms:ajout-dossier';
const DRAFT_VERSION = 1;

type SerializedSiteInformations = Omit<
  Partial<SiteInformationsType>,
  'dateDebutTravaux' | 'dateFinTravaux'
> & {
  dateDebutTravaux?: string;
  dateFinTravaux?: string;
};

type PersistedBeneficiaryValues = Omit<Partial<BeneficiaryFormType>, 'files'>;

interface AjoutDossierDraft {
  currentSlide: number;
  isSiteStepComplete: boolean;
  step1Values?: SerializedSiteInformations;
  projectValues?: Partial<ProjectInformationsFormType>;
  beneficiaryValues?: PersistedBeneficiaryValues;
}

interface PersistDraftPatchOptions {
  persistFiles?: boolean;
  files?: BeneficiaryFormType['files'];
}

interface UseAjoutDossierDraftParams {
  openRequestId: number;
  siteContactFormInstance: FormInstance<SiteContactInformationsType>;
  siteAddressFormInstance: FormInstance<SiteAddressInformationsType>;
  projectFormInstance: FormInstance<ProjectInformationsFormType>;
  beneficiaryFormInstance: FormInstance<BeneficiaryFormType>;
  resetForms: () => void;
  resetMutationState: () => void;
  onBeforeOpen?: () => void;
}

interface UseAjoutDossierDraftReturn {
  state: {
    isHydrated: boolean;
    isDraftActive: boolean;
    isDraftMinimized: boolean;
    currentSlide: number;
    isSiteStepComplete: boolean;
    siteContactInfo?: SiteContactInfo;
  };
  actions: {
    expandDraft: () => void;
    minimizeDraft: () => void;
    clearDraft: () => void;
    clearPersistedDraft: () => void;
    clearPersistedFiles: () => void;
    persistCurrentValues: () => void;
    updateCurrentSlide: (nextSlide: number) => void;
    handleSiteCompletionChange: (isComplete: boolean) => void;
    handleSiteValuesChange: (
      _: Partial<SiteInformationsType>,
      allValues: SiteInformationsType,
    ) => void;
    handleProjectValuesChange: (
      _: Partial<ProjectInformationsFormType>,
      allValues: ProjectInformationsFormType,
    ) => void;
    handleBeneficiaryValuesChange: (
      changedValues: Partial<BeneficiaryFormType>,
      allValues: BeneficiaryFormType,
    ) => void;
  };
}

function createInitialDraft(): AjoutDossierDraft {
  return {
    currentSlide: 0,
    isSiteStepComplete: false,
    step1Values: undefined,
    projectValues: undefined,
    beneficiaryValues: undefined,
  };
}

function serializeSiteInformations(
  values?: Partial<SiteInformationsType>,
): SerializedSiteInformations | undefined {
  if (!values) {
    return undefined;
  }

  return {
    ...values,
    dateDebutTravaux: values.dateDebutTravaux
      ? dayjs(values.dateDebutTravaux).toISOString()
      : undefined,
    dateFinTravaux: values.dateFinTravaux
      ? dayjs(values.dateFinTravaux).toISOString()
      : undefined,
  };
}

function deserializeSiteInformations(
  values?: SerializedSiteInformations,
): Partial<SiteInformationsType> | undefined {
  if (!values) {
    return undefined;
  }

  return {
    ...values,
    dateDebutTravaux: values.dateDebutTravaux
      ? dayjs(values.dateDebutTravaux)
      : undefined,
    dateFinTravaux: values.dateFinTravaux
      ? dayjs(values.dateFinTravaux)
      : undefined,
  };
}

function sanitizeBeneficiaryValues(
  values?: Partial<BeneficiaryFormType>,
): PersistedBeneficiaryValues | undefined {
  if (!values) {
    return undefined;
  }

  const sanitizedValues = { ...values } as Partial<BeneficiaryFormType>;
  delete sanitizedValues.files;
  return sanitizedValues as PersistedBeneficiaryValues;
}

export default function useAjoutDossierDraft({
  openRequestId,
  siteContactFormInstance,
  siteAddressFormInstance,
  projectFormInstance,
  beneficiaryFormInstance,
  resetForms,
  resetMutationState,
  onBeforeOpen,
}: UseAjoutDossierDraftParams): UseAjoutDossierDraftReturn {
  const { auth } = useAuthContext();

  const {
    isHydrated,
    storageKey,
    state: draftState,
    startDraft,
    patchDraft,
    minimizeDraft,
    expandDraft,
    clearDraft: resetDraftState,
    clearPersistedDraft,
  } = useMinimizablePersistedDraft<AjoutDossierDraft>({
    storageNamespace: DRAFT_STORAGE_NAMESPACE,
    userScope: auth?.user?.id,
    draftVersion: DRAFT_VERSION,
    expandOnRestore: false,
    createInitialDraft,
  });

  const {
    persistFiles: persistBeneficiaryFiles,
    restoreFiles: restoreBeneficiaryFiles,
    clearFiles,
  } = useIndexedDbUploadDraft({
    storageKey,
    entry: `beneficiary-files:v${DRAFT_VERSION}`,
  });

  const currentSlide = draftState.draft.currentSlide;
  const isSiteStepComplete = draftState.draft.isSiteStepComplete;
  const isDraftActive = draftState.isActive;

  const step1Values = useMemo(
    () => deserializeSiteInformations(draftState.draft.step1Values),
    [draftState.draft.step1Values],
  );

  const siteContactInfo = useMemo(() => {
    if (
      !step1Values?.interlocuteurChantierLastname ||
      !step1Values.interlocuteurChantierFirstname ||
      !step1Values.interlocuteurChantierEmail
    ) {
      return undefined;
    }

    return {
      lastname: step1Values.interlocuteurChantierLastname,
      firstname: step1Values.interlocuteurChantierFirstname,
      email: step1Values.interlocuteurChantierEmail,
    } satisfies SiteContactInfo;
  }, [step1Values]);

  const persistDraftPatch = useCallback(
    (
      patch: Partial<AjoutDossierDraft>,
      options: PersistDraftPatchOptions = {},
    ) => {
      // On ne persiste rien tant que le draft n'est pas officiellement actif.
      if (!isDraftActive) {
        return;
      }

      patchDraft(patch);
      if (options.persistFiles) {
        // Les fichiers vivent dans IndexedDB (pas dans localStorage).
        persistBeneficiaryFiles(options.files).catch(() => undefined);
      }
    },
    [isDraftActive, patchDraft, persistBeneficiaryFiles],
  );

  const persistCurrentValues = useCallback(() => {
    if (!isDraftActive) {
      return;
    }

    const beneficiaryValues = beneficiaryFormInstance.getFieldsValue(
      true,
    ) as Partial<BeneficiaryFormType>;
    // Upload peut ne pas toujours remonter dans getFieldsValue(true)
    // selon le timing des événements; on lit explicitement le champ.
    const beneficiaryFiles = beneficiaryFormInstance.getFieldValue('files') as
      | BeneficiaryFormType['files']
      | undefined;
    const beneficiaryValuesWithFiles: Partial<BeneficiaryFormType> = {
      ...beneficiaryValues,
      files: beneficiaryFiles ?? beneficiaryValues.files,
    };

    persistDraftPatch(
      {
        step1Values: serializeSiteInformations({
          ...siteContactFormInstance.getFieldsValue(true),
          ...siteAddressFormInstance.getFieldsValue(true),
        } as Partial<SiteInformationsType>),
        projectValues: projectFormInstance.getFieldsValue(
          true,
        ) as Partial<ProjectInformationsFormType>,
        beneficiaryValues: sanitizeBeneficiaryValues(
          beneficiaryValuesWithFiles,
        ),
      },
      {
        persistFiles: true,
        files: beneficiaryValuesWithFiles.files,
      },
    );
  }, [
    beneficiaryFormInstance,
    isDraftActive,
    persistDraftPatch,
    projectFormInstance,
    siteAddressFormInstance,
    siteContactFormInstance,
  ]);

  const updateCurrentSlide = useCallback(
    (nextSlide: number) => {
      persistDraftPatch({ currentSlide: nextSlide });
    },
    [persistDraftPatch],
  );

  const handleSiteCompletionChange = useCallback(
    (isComplete: boolean) => {
      persistDraftPatch({ isSiteStepComplete: isComplete });
    },
    [persistDraftPatch],
  );

  const handleSiteValuesChange = useCallback(
    (_: Partial<SiteInformationsType>, allValues: SiteInformationsType) => {
      persistDraftPatch({
        step1Values: serializeSiteInformations(allValues),
      });
    },
    [persistDraftPatch],
  );

  const handleProjectValuesChange = useCallback(
    (
      _: Partial<ProjectInformationsFormType>,
      allValues: ProjectInformationsFormType,
    ) => {
      persistDraftPatch({
        projectValues: allValues,
      });
    },
    [persistDraftPatch],
  );

  const handleBeneficiaryValuesChange = useCallback(
    (
      changedValues: Partial<BeneficiaryFormType>,
      allValues: BeneficiaryFormType,
    ) => {
      const filesChanged = Object.prototype.hasOwnProperty.call(
        changedValues,
        'files',
      );
      persistDraftPatch(
        {
          beneficiaryValues: sanitizeBeneficiaryValues(allValues),
        },
        {
          persistFiles: filesChanged,
          files: filesChanged ? allValues.files : undefined,
        },
      );
    },
    [persistDraftPatch],
  );

  const lastOpenRequestRef = useRef(openRequestId);
  const hydratedDraftRef = useRef(false);
  const forceFreshDraftOnNextOpenRef = useRef(false);

  const clearDraft = useCallback(() => {
    forceFreshDraftOnNextOpenRef.current = true;
    resetDraftState();
  }, [resetDraftState]);

  const hydrateFormsFromDraft = useCallback(
    (draft: AjoutDossierDraft) => {
      resetForms();

      const draftSiteValues = deserializeSiteInformations(draft.step1Values);
      if (draftSiteValues) {
        siteContactFormInstance.setFieldsValue({
          interlocuteurChantierLastname:
            draftSiteValues.interlocuteurChantierLastname,
          interlocuteurChantierFirstname:
            draftSiteValues.interlocuteurChantierFirstname,
          interlocuteurChantierEmail:
            draftSiteValues.interlocuteurChantierEmail,
          raisonSocialeBeneficiaire: draftSiteValues.raisonSocialeBeneficiaire,
        });
        siteAddressFormInstance.setFieldsValue({
          nomSite: draftSiteValues.nomSite,
          adresseSite: draftSiteValues.adresseSite,
          codePostalSite: draftSiteValues.codePostalSite,
          villeSite: draftSiteValues.villeSite,
          dateDebutTravaux: draftSiteValues.dateDebutTravaux,
          dateFinTravaux: draftSiteValues.dateFinTravaux,
        });
      }
      if (draft.projectValues) {
        projectFormInstance.setFieldsValue(
          draft.projectValues as ProjectInformationsFormType,
        );
      }
      if (draft.beneficiaryValues) {
        beneficiaryFormInstance.setFieldsValue(
          draft.beneficiaryValues as BeneficiaryFormType,
        );
      }
    },
    [
      beneficiaryFormInstance,
      projectFormInstance,
      resetForms,
      siteAddressFormInstance,
      siteContactFormInstance,
    ],
  );

  useEffect(() => {
    // `openRequestId` agit comme un trigger d'ouverture. Le ref évite de rejouer
    // la logique à chaque render avec la même valeur.
    if (openRequestId === lastOpenRequestRef.current) {
      return;
    }

    lastOpenRequestRef.current = openRequestId;
    if (openRequestId <= 0) {
      return;
    }

    onBeforeOpen?.();
    if (draftState.isActive && !forceFreshDraftOnNextOpenRef.current) {
      expandDraft();
      return;
    }

    forceFreshDraftOnNextOpenRef.current = false;
    startDraft(createInitialDraft());
  }, [
    draftState.isActive,
    expandDraft,
    onBeforeOpen,
    openRequestId,
    startDraft,
  ]);

  useEffect(() => {
    if (!isHydrated || !isDraftActive) {
      hydratedDraftRef.current = false;
      return;
    }

    // Hydratation one-shot par activation du draft pour éviter d'écraser des saisies en cours.
    if (hydratedDraftRef.current) {
      return;
    }

    hydratedDraftRef.current = true;
    hydrateFormsFromDraft(draftState.draft);
    resetMutationState();

    let isCancelled = false;
    const restorePersistedBeneficiaryFiles = async () => {
      const files = await restoreBeneficiaryFiles();
      if (isCancelled) {
        return;
      }

      // Ne réinjecte pas les fichiers restaurés si l'utilisateur en a déjà ajouté localement.
      const currentFiles = beneficiaryFormInstance.getFieldValue('files') as
        | BeneficiaryFormType['files']
        | undefined;
      if (currentFiles?.length) {
        return;
      }

      beneficiaryFormInstance.setFieldsValue({
        files: files.length ? files : undefined,
      });
    };

    restorePersistedBeneficiaryFiles().catch(() => undefined);

    return () => {
      isCancelled = true;
    };
  }, [
    beneficiaryFormInstance,
    draftState.draft,
    hydrateFormsFromDraft,
    isDraftActive,
    isHydrated,
    resetMutationState,
    restoreBeneficiaryFiles,
  ]);

  const clearPersistedFiles = useCallback(() => {
    clearFiles().catch(() => undefined);
  }, [clearFiles]);

  return {
    state: {
      isHydrated,
      isDraftActive,
      isDraftMinimized: draftState.isMinimized,
      currentSlide,
      isSiteStepComplete,
      siteContactInfo,
    },
    actions: {
      expandDraft,
      minimizeDraft,
      clearDraft,
      clearPersistedDraft,
      clearPersistedFiles,
      persistCurrentValues,
      updateCurrentSlide,
      handleSiteCompletionChange,
      handleSiteValuesChange,
      handleProjectValuesChange,
      handleBeneficiaryValuesChange,
    },
  };
}
